/**
 * Conversion Context
 * Manages conversion mode state, preferences, and history
 * Follows design system: Tech Minimalism / Neo-Brutalism Light
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

// Types
export type ConversionMode = 'n8n-to-python' | 'python-to-n8n';

export interface ConversionHistoryItem {
  id: string;
  mode: ConversionMode;
  timestamp: Date;
  input: string;
  output: string;
  success: boolean;
  metadata?: {
    inputSize: number;
    outputSize: number;
    conversionTime: number;
  };
}

export interface UserPreferences {
  defaultMode: ConversionMode;
  autoSave: boolean;
  showAdvancedOptions: boolean;
  theme: 'light' | 'dark' | 'system';
}

export interface ConversionContextValue {
  mode: ConversionMode;
  setMode: (mode: ConversionMode) => void;
  isConverting: boolean;
  setIsConverting: (converting: boolean) => void;
  conversionHistory: ConversionHistoryItem[];
  addToHistory: (item: Omit<ConversionHistoryItem, 'id' | 'timestamp'>) => void;
  clearHistory: () => void;
  preferences: UserPreferences;
  updatePreferences: (prefs: Partial<UserPreferences>) => void;
  hasUnsavedInput: boolean;
  setHasUnsavedInput: (hasInput: boolean) => void;
}

// Default preferences
const defaultPreferences: UserPreferences = {
  defaultMode: 'n8n-to-python',
  autoSave: true,
  showAdvancedOptions: false,
  theme: 'system'
};

// Storage keys
const STORAGE_KEYS = {
  MODE: 'n8n2py_conversion_mode',
  PREFERENCES: 'n8n2py_preferences',
  HISTORY: 'n8n2py_conversion_history'
};

// Create context
const ConversionContext = createContext<ConversionContextValue | undefined>(undefined);

// Provider props
interface ConversionProviderProps {
  children: ReactNode;
}

/**
 * ConversionProvider
 * Provides conversion state and preferences to the application
 */
export function ConversionProvider({ children }: ConversionProviderProps) {
  // Load initial mode from localStorage or use default
  const [mode, setModeState] = useState<ConversionMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.MODE);
    return (stored as ConversionMode) || 'n8n-to-python';
  });

  // Load preferences from localStorage
  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.PREFERENCES);
    if (stored) {
      try {
        return { ...defaultPreferences, ...JSON.parse(stored) };
      } catch (error) {
        console.warn('Failed to parse preferences from localStorage:', error);
      }
    }
    return defaultPreferences;
  });

  // Load history from localStorage
  const [conversionHistory, setConversionHistory] = useState<ConversionHistoryItem[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.HISTORY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Convert timestamp strings back to Date objects
        return parsed.map((item: any) => ({
          ...item,
          timestamp: new Date(item.timestamp)
        }));
      } catch (error) {
        console.warn('Failed to parse history from localStorage:', error);
      }
    }
    return [];
  });

  const [isConverting, setIsConverting] = useState(false);
  const [hasUnsavedInput, setHasUnsavedInput] = useState(false);

  // Persist mode to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.MODE, mode);
  }, [mode]);

  // Persist preferences to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.PREFERENCES, JSON.stringify(preferences));
  }, [preferences]);

  // Persist history to localStorage (keep last 50 items)
  useEffect(() => {
    const historyToStore = conversionHistory.slice(0, 50);
    localStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(historyToStore));
  }, [conversionHistory]);

  // Set mode with validation
  const setMode = useCallback((newMode: ConversionMode) => {
    setModeState(newMode);
  }, []);

  // Add item to history
  const addToHistory = useCallback((item: Omit<ConversionHistoryItem, 'id' | 'timestamp'>) => {
    const newItem: ConversionHistoryItem = {
      ...item,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    };

    setConversionHistory(prev => [newItem, ...prev]);
  }, []);

  // Clear history
  const clearHistory = useCallback(() => {
    setConversionHistory([]);
    localStorage.removeItem(STORAGE_KEYS.HISTORY);
  }, []);

  // Update preferences
  const updatePreferences = useCallback((prefs: Partial<UserPreferences>) => {
    setPreferences(prev => ({ ...prev, ...prefs }));
  }, []);

  // Cross-tab synchronization using BroadcastChannel
  useEffect(() => {
    // Check if BroadcastChannel is supported
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    const channel = new BroadcastChannel('n8n2py_conversion_sync');

    // Listen for messages from other tabs
    channel.onmessage = (event) => {
      const { type, data } = event.data;

      switch (type) {
        case 'MODE_CHANGE':
          setModeState(data.mode);
          break;
        case 'PREFERENCES_UPDATE':
          setPreferences(prev => ({ ...prev, ...data.preferences }));
          break;
        case 'HISTORY_UPDATE':
          setConversionHistory(data.history.map((item: any) => ({
            ...item,
            timestamp: new Date(item.timestamp)
          })));
          break;
      }
    };

    return () => {
      channel.close();
    };
  }, []);

  // Broadcast mode changes to other tabs
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    const channel = new BroadcastChannel('n8n2py_conversion_sync');
    channel.postMessage({ type: 'MODE_CHANGE', data: { mode } });
    channel.close();
  }, [mode]);

  // Broadcast preference changes to other tabs
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    const channel = new BroadcastChannel('n8n2py_conversion_sync');
    channel.postMessage({ type: 'PREFERENCES_UPDATE', data: { preferences } });
    channel.close();
  }, [preferences]);

  const value: ConversionContextValue = {
    mode,
    setMode,
    isConverting,
    setIsConverting,
    conversionHistory,
    addToHistory,
    clearHistory,
    preferences,
    updatePreferences,
    hasUnsavedInput,
    setHasUnsavedInput
  };

  return (
    <ConversionContext.Provider value={value}>
      {children}
    </ConversionContext.Provider>
  );
}

/**
 * useConversion hook
 * Access conversion context from any component
 */
export function useConversion() {
  const context = useContext(ConversionContext);
  
  if (context === undefined) {
    throw new Error('useConversion must be used within a ConversionProvider');
  }
  
  return context;
}

// Export context for testing
export { ConversionContext };
